import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { userAvatar, teacherAvatar } from '../assets/teacherAvatar';
import { 
  sendTechnicalInterviewMessage, 
  sendBehavioralInterviewMessage, 
  createInterviewerSystemMessage, 
  createTechnicalInterviewerSystemMessage 
} from '../services/openaiService';
import { speak, getVoices, initializeSpeechSynthesis } from '../services/speechService';
import { getApiBaseUrl } from '../services/envService';
import useLocalStorage from '../hooks/useLocalStorage';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

// Create context
const ChatContext = createContext();

// Custom hook to use chat context
export const useChat = () => useContext(ChatContext);

// Provider component
export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [isInitialized, setIsInitialized] = useState(() => !!getApiBaseUrl());
  const [apiKey, setApiKey] = useLocalStorage('openai_api_key', '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [pdfText, setPdfText] = useState('');
  const [hasPdfContent, setHasPdfContent] = useState(false);
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [interviewScore, setInterviewScore] = useState(null);
  
  // New state for interview types and technical requirements
  const [interviewType, setInterviewType] = useState('behavioral'); // 'behavioral' or 'technical'
  const [techRequirements, setTechRequirements] = useState('');
  const [hasTechRequirements, setHasTechRequirements] = useState(false);
  
  const conversationHistoryRef = useRef([]);

  // Speech recognition from react-speech-recognition
  const {
    transcript,
    listening: isRecording,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  // Add a flag to track whether we've already processed a message for this recording session
  const [messageProcessed, setMessageProcessed] = useState(false);

  // Initialize voices
  useEffect(() => {
    const loadVoices = async () => {
      const availableVoices = await getVoices();
      setVoices(availableVoices);
      
      // Use a neutral voice
      const englishVoice = availableVoices.find(voice => voice.lang.includes('en'));
      if (englishVoice) {
        setSelectedVoice(englishVoice);
      } else {
        setSelectedVoice(availableVoices[0]);
      }
    };
    
    loadVoices();
  }, []);

  // Check browser support
  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      setError('Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.');
    }
  }, [browserSupportsSpeechRecognition]);

  // Speak a message using the selected voice
  const speakMessage = useCallback(async (text) => {
    if (!text) return;
    
    setIsSpeaking(true);
    try {
      await speak(text, 1, 1, selectedVoice);
    } catch (error) {
      // Silently handle errors
    } finally {
      setIsSpeaking(false);
    }
  }, [selectedVoice]);

  // Update PDF text
  const setPdfContent = useCallback((text) => {
    setPdfText(text);
    setHasPdfContent(!!text);
  }, []);

  // Set technical requirements
  const setTechRequirementsContent = useCallback((text) => {
    setTechRequirements(text);
    setHasTechRequirements(!!text);
  }, []);

  // Create system message based on current state and interview type
  const createSystemMessage = useCallback(() => {
    let baseSystemMessage;
    
    if (interviewType === 'technical') {
      baseSystemMessage = createTechnicalInterviewerSystemMessage();
      
      if (hasTechRequirements && techRequirements) {
        return {
          role: "system",
          content: `${baseSystemMessage.content}\n\nTechnical requirements and focus areas for this interview:\n\n${techRequirements.substring(0, 8000)}\n\nBase your technical questions specifically on these technologies, frameworks, and requirements. Focus on practical implementation, best practices, and real-world scenarios related to this tech stack.`
        };
      }
    } else {
      // Behavioral interview
      baseSystemMessage = createInterviewerSystemMessage();
      
      if (hasPdfContent && pdfText) {
        return {
          role: "system",
          content: `${baseSystemMessage.content}\n\nInformation about the candidate or position:\n\n${pdfText.substring(0, 10000)}\n\nUse this information to tailor your interview questions to the candidate's background and the position requirements. Ask specific questions that relate to their experience and skills as described in this document.`
        };
      }
    }
    
    return baseSystemMessage;
  }, [interviewType, hasPdfContent, pdfText, hasTechRequirements, techRequirements]);

  // Start a new interview conversation
  const startConversation = useCallback(async () => {
    if (!isInitialized) {
      setError('The interview server is not configured. Set INTERVIEW_API_BASE_URL in config.js.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInterviewComplete(false);
    setInterviewScore(null);

    try {
      // Initialize conversation history with system message
      conversationHistoryRef.current = [createSystemMessage()];
      
      let initialMessages = [];
      
      // Add STAR method cards for behavioral interviews
      if (interviewType === 'behavioral') {
        const starCards = [
          {
            id: 'star-1',
            content: '🎯 **STAR Method**: Structure your answers using **S**ituation, **T**ask, **A**ction, **R**esult for clear, compelling responses.',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isUser: false,
            status: 'read',
            isInfoCard: true
          },
          {
            id: 'star-2', 
            content: '💡 **Example Format**: "In my previous role **(Situation)**, I needed to **(Task)**. I approached this by **(Action)**, which resulted in **(Result)**."',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isUser: false,
            status: 'read',
            isInfoCard: true
          },
          {
            id: 'star-3',
            content: '🚀 **Pro Tips**: Use specific examples, quantify results when possible, and focus on your individual contributions to team efforts.',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isUser: false,
            status: 'read',
            isInfoCard: true
          }
        ];
        
        initialMessages = [...starCards];
      }
      
      // Get initial response from GPT
      let response;
      if (interviewType === 'behavioral') {
        response = await sendBehavioralInterviewMessage(conversationHistoryRef.current);
      } else {
        response = await sendTechnicalInterviewMessage(conversationHistoryRef.current);
      }
      
      // Add interviewer's initial message to the conversation
      const initialMessage = {
        id: initialMessages.length + 1,
        content: response,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isUser: false,
        status: 'read'
      };
      
      setMessages([...initialMessages, initialMessage]);
      
      // Add the assistant's response to the conversation history
      conversationHistoryRef.current.push({
        role: "assistant",
        content: response
      });
      
      // Mark conversation as started
      setConversationStarted(true);
      
      // Speak the response
      speakMessage(response);
    } catch (error) {
      setError(error.message || 'Failed to start interview. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, speakMessage, createSystemMessage, interviewType]);

  useEffect(() => {
    if (getApiBaseUrl()) {
      setIsInitialized(true);
      setError(null);
    } else {
      setIsInitialized(false);
      setError('Interview server URL is missing. Set INTERVIEW_API_BASE_URL in config.js.');
    }
  }, []);

  // Add a message to the UI
  const addMessage = useCallback((content, isUser) => {
    const newMessage = {
      id: messages.length + 1,
      content,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isUser,
      status: 'sent'
    };
    
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, [messages.length]);

  // Update message status
  const updateMessageStatus = useCallback((id, status) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === id ? { ...msg, status } : msg
      )
    );
  }, []);

  // Start recording user's voice
  const startVoiceRecording = () => {
    if (isRecording || !browserSupportsSpeechRecognition || !isMicrophoneAvailable || interviewComplete) return;
    
    // Initialize speech synthesis for mobile Safari
    initializeSpeechSynthesis();
    
    // Reset the message processed flag at the start of recording
    setMessageProcessed(false);
    
    // Reset transcript and start listening
    resetTranscript();
    SpeechRecognition.startListening({ 
      continuous: true,
      language: 'en-US'
    });
  };

  // Stop recording user's voice
  const stopVoiceRecording = () => {
    if (!isRecording) return;
    
    // Stop speech recognition
    SpeechRecognition.stopListening();
  };

  // Check if response indicates interview is complete (updated for both interview types)
  const checkForInterviewCompletion = useCallback((response) => {
    const isComplete = response.includes("INTERVIEW COMPLETE") || response.includes("TECHNICAL INTERVIEW COMPLETE");
    
    if (isComplete) {
      setInterviewComplete(true);
      
      // Extract score - support both formats
      const scoreMatch = response.match(/Your (?:interview|technical interview) score: ([A-F][+]?)/);
      if (scoreMatch && scoreMatch[1]) {
        setInterviewScore(scoreMatch[1]);
      }
      
      return true;
    }
    return false;
  }, []);

  // Process response from OpenAI
  const processOpenAIResponse = useCallback(async (userMessageId) => {
    try {
      let response;
      if (interviewType === 'behavioral') {
        response = await sendBehavioralInterviewMessage(conversationHistoryRef.current);
      } else {
        response = await sendTechnicalInterviewMessage(conversationHistoryRef.current);
      }

      // Add the response to the conversation history
      conversationHistoryRef.current.push({
        role: "assistant",
        content: response
      });

      // Check if interview is complete
      checkForInterviewCompletion(response);

      // Add the response to messages
      addMessage(response, false);
      
      // Mark user message as read
      updateMessageStatus(userMessageId, 'read');
      
      // Speak the response
      speakMessage(response);
    } catch (error) {
      updateMessageStatus(userMessageId, 'error');
      setError(error.message || 'Failed to get response. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [checkForInterviewCompletion, addMessage, updateMessageStatus, speakMessage, interviewType]);

  // Send a message to the conversation
  const sendMessage = useCallback(async (content) => {
    if (!isInitialized) {
      setError('The interview server is not configured. Set INTERVIEW_API_BASE_URL in config.js.');
      return;
    }

    // Start conversation if not started yet
    if (!conversationStarted) {
      await startConversation();
      return;
    }

    // Don't send empty messages or if interview is already complete
    if (!content || content.trim().length === 0 || interviewComplete) return;

    setIsLoading(true);
    setError(null);

    // Add user message to UI
    const userMessage = addMessage(content, true);

    // Add the message to the conversation history
    conversationHistoryRef.current.push({
      role: "user",
      content
    });

    // Get response from OpenAI
    processOpenAIResponse(userMessage.id);
  }, [isInitialized, conversationStarted, interviewComplete, startConversation, addMessage, processOpenAIResponse]);

  // Handle transcript changes automatically
  useEffect(() => {
    // Only process if we're not recording (meaning we just stopped) and haven't processed yet
    if (!isRecording && transcript && transcript.trim() && !messageProcessed) {
      const processedText = transcript.trim();
      if (processedText.length > 0) {
        console.log("Using transcript from voice recording:", processedText);
        setMessageProcessed(true); // Mark as processed
        sendMessage(processedText);
      }
    }
  }, [isRecording, transcript, messageProcessed, sendMessage]);

  // Set a new OpenAI API key
  const setOpenAIKey = (key) => {
    setApiKey(key);
  };

  // Change the selected voice
  const changeVoice = (voice) => {
    setSelectedVoice(voice);
  };

  // Reset the conversation (updated to handle interview type)
  const resetConversation = () => {
    setMessages([]);
    conversationHistoryRef.current = [];
    setConversationStarted(false);
    setInterviewComplete(false);
    setInterviewScore(null);
    // Keep PDF content and tech requirements based on interview type
  };

  // Set interview type
  const setInterviewTypeMode = useCallback((type) => {
    setInterviewType(type);
    // Reset conversation when switching types
    resetConversation();
  }, []);

  return (
    <ChatContext.Provider
      value={{
        messages,
        isInitialized,
        isLoading,
        error,
        apiKey,
        voices,
        selectedVoice,
        isRecording,
        isSpeaking,
        hasPdfContent,
        pdfText,
        userAvatar,
        teacherAvatar,
        hasApiKey: isInitialized,
        interviewComplete,
        interviewScore,
        interviewType,
        techRequirements,
        hasTechRequirements,
        setOpenAIKey,
        sendMessage,
        speakMessage,
        startVoiceRecording,
        stopVoiceRecording,
        changeVoice,
        resetConversation,
        startConversation,
        conversationStarted,
        setPdfContent,
        setTechRequirementsContent,
        setInterviewTypeMode
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext; 